/**
 * MiniTrendChart — inline sparkline showing the last N numeric values
 * with an optional threshold reference line.
 *
 * Built for "last 5 corner counts", "shots-on-target trend", "odds
 * movement", "goals-per-game trend" — anywhere the editorial point is
 * "did the values cluster above or below this line?".
 *
 * Visual rules:
 *   - Solid polyline through the N points.
 *   - Optional dashed horizontal line at `threshold` (e.g. "Over 2,5"),
 *     with an optional label rendered above the line at the right edge.
 *   - Each point is a small filled circle. The most recent point
 *     (rightmost) gets a 1.5× larger marker so "what just happened"
 *     reads at a glance.
 *   - If `aboveColor`/`belowColor` are supplied, points are coloured by
 *     which side of the threshold they fall on. Without those, every
 *     point uses the line `color`.
 *   - Animation: line draws left-to-right via `stroke-dashoffset`
 *     (~400ms, ease-out). Respects `prefers-reduced-motion`.
 *
 * Defaults to 100×40 px so it fits inside compact card columns. Tabular
 * — no axes, no grid; the chart is a glance, not a study.
 *
 * Shape (LLM-portable):
 *   {
 *     values: number[];
 *     threshold?: number;
 *     thresholdLabel?: string;
 *     color?: string;
 *     aboveColor?: string;
 *     belowColor?: string;
 *     width?: number;          // default 100
 *     height?: number;         // default 40
 *     ariaLabel: string;
 *   }
 */

import { useId, useMemo, type CSSProperties, type ReactElement } from 'react';
import { z } from 'zod';
import type { PrimitiveMeta } from './_meta.js';
import styles from './MiniTrendChart.module.css';

const DEFAULT_WIDTH = 100;
const DEFAULT_HEIGHT = 40;
const STROKE = 1.75;
const POINT_R = 1.75;
const POINT_LAST_R = POINT_R * 1.5; // visual "most recent" marker
const PADDING_X = 4;
const PADDING_Y = 5;

const dataShape = z.object({
  /** Ordered oldest → newest. Min 2 points so the line has a direction. */
  values: z.array(z.number()).min(2).max(20),
  /** Horizontal reference line value, drawn dashed when supplied. */
  threshold: z.number().optional(),
  /** Tiny label rendered next to the threshold line. */
  thresholdLabel: z.string().optional(),
  /** Default line + point colour. Falls back to --accent-primary. */
  color: z.string().optional(),
  /** Optional split: points above threshold use this colour. */
  aboveColor: z.string().optional(),
  /** Optional split: points below threshold use this colour. */
  belowColor: z.string().optional(),
  /** SVG viewport width in px. Default 100. */
  width: z.number().int().positive().optional(),
  /** SVG viewport height in px. Default 40. */
  height: z.number().int().positive().optional(),
  ariaLabel: z.string(),
});

export const META: PrimitiveMeta = {
  name: 'mini-trend-chart',
  description:
    'Inline sparkline of last-N values with an optional threshold rule.',
  composable: true,
  dataShape,
  densities: ['compact', 'standard'],
  breakpoints: { mobile: true, wide: true },
  animation: 'reveal',
};

export interface MiniTrendChartProps {
  /** Ordered oldest → newest. At least 2 values required visually. */
  values: number[];
  threshold?: number;
  thresholdLabel?: string;
  color?: string;
  aboveColor?: string;
  belowColor?: string;
  width?: number;
  height?: number;
  ariaLabel: string;
}

interface PointPx {
  x: number;
  y: number;
}

/** Build pixel-space points + threshold-y from the data + viewport. */
function buildGeometry(
  values: number[],
  width: number,
  height: number,
  threshold: number | undefined,
): { points: PointPx[]; thresholdY: number | null; minV: number; maxV: number } {
  const minV = Math.min(...values, threshold ?? Number.POSITIVE_INFINITY);
  const maxV = Math.max(...values, threshold ?? Number.NEGATIVE_INFINITY);
  // Flat data is legal — give it a 1-unit span so the line stays mid-height.
  const span = Math.abs(maxV - minV) < 1e-9 ? 1 : maxV - minV;

  const innerW = width - PADDING_X * 2;
  const innerH = height - PADDING_Y * 2;
  const stepX = values.length > 1 ? innerW / (values.length - 1) : 0;

  const toY = (v: number): number =>
    PADDING_Y + (1 - (v - minV) / span) * innerH;

  const points: PointPx[] = values.map((v, i) => ({
    x: PADDING_X + i * stepX,
    y: toY(v),
  }));

  const thresholdY = threshold === undefined ? null : toY(threshold);
  return { points, thresholdY, minV, maxV };
}

/** Approximate polyline length so the dash-offset draw animation reads
 *  smoothly on first paint without `getBBox`. */
function approxLength(points: PointPx[]): number {
  let len = 0;
  for (let i = 1; i < points.length; i++) {
    const dx = points[i]!.x - points[i - 1]!.x;
    const dy = points[i]!.y - points[i - 1]!.y;
    len += Math.sqrt(dx * dx + dy * dy);
  }
  return Math.max(1, len);
}

function pickPointColor(
  value: number,
  threshold: number | undefined,
  baseColor: string,
  aboveColor: string | undefined,
  belowColor: string | undefined,
): string {
  if (threshold === undefined) return baseColor;
  if (value >= threshold) return aboveColor ?? baseColor;
  return belowColor ?? baseColor;
}

export function MiniTrendChart({
  values,
  threshold,
  thresholdLabel,
  color,
  aboveColor,
  belowColor,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  ariaLabel,
}: MiniTrendChartProps): ReactElement {
  const uid = useId();
  const lineColor = color ?? 'var(--accent-primary, #2A46D4)';
  const thresholdStroke = 'var(--text-muted, #6e6e75)';

  const geom = useMemo(
    () => buildGeometry(values, width, height, threshold),
    [values, width, height, threshold],
  );

  // No points → empty render. Caller decides whether to hide upstream.
  if (geom.points.length === 0) {
    return (
      <span className={styles.wrap} role="img" aria-label={ariaLabel}>
        <svg
          className={styles.svg}
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          aria-hidden="true"
        />
      </span>
    );
  }

  const polylinePoints = geom.points.map((p) => `${p.x},${p.y}`).join(' ');
  const len = approxLength(geom.points);
  const animStyle = {
    '--aamp-mtc-len': len,
    strokeDasharray: len,
    strokeDashoffset: 0,
  } as CSSProperties & Record<'--aamp-mtc-len', number>;

  const lastIdx = geom.points.length - 1;
  const summary =
    threshold === undefined
      ? `${values.length} values from ${values[0]} to ${values[lastIdx]}`
      : `${values.length} values plotted against threshold ${threshold}`;

  return (
    <span className={styles.wrap} role="img" aria-label={ariaLabel}>
      <svg
        className={styles.svg}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        aria-hidden="true"
      >
        {/* Threshold line (under the polyline). Paint via `style` — SVG
            presentation attributes do not resolve the `var()` colour
            strings used for stroke/fill in this component. */}
        {geom.thresholdY !== null && (
          <>
            <line
              className={styles.threshold}
              x1={PADDING_X}
              x2={width - PADDING_X}
              y1={geom.thresholdY}
              y2={geom.thresholdY}
              style={{ stroke: thresholdStroke }}
              strokeWidth={1}
            />
            {thresholdLabel !== undefined && thresholdLabel.length > 0 && (
              <text
                className={styles.thresholdLabel}
                x={width - PADDING_X}
                y={geom.thresholdY - 3}
                textAnchor="end"
              >
                {thresholdLabel}
              </text>
            )}
          </>
        )}

        {/* Polyline — solid line with reveal animation. */}
        <polyline
          className={`${styles.line} ${styles.lineReveal}`}
          points={polylinePoints}
          strokeWidth={STROKE}
          style={{ ...animStyle, stroke: lineColor }}
        />

        {/* Points. */}
        {geom.points.map((p, i) => {
          const isLast = i === lastIdx;
          const fill = pickPointColor(
            values[i]!,
            threshold,
            lineColor,
            aboveColor,
            belowColor,
          );
          return (
            <circle
              key={`${uid}-p-${i}`}
              className={isLast ? styles.pointLast : styles.point}
              cx={p.x}
              cy={p.y}
              r={isLast ? POINT_LAST_R : POINT_R}
              style={{ fill }}
            />
          );
        })}
      </svg>
      <span className={styles.srOnly}>{summary}</span>
    </span>
  );
}

export default MiniTrendChart;
